/**
 * 匿名登录
 * 开平token获取
 * 获取用户信息
 */
import { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { isNative, isOverseas } from '@whelk/const/src/env';
import { checkIsNeedTokenPath, HOME_URL } from '@whelk/const/src/routerUrl';
import {
  LOGIN_TYPE,
  getNewToken,
  getCurrentUser,
  getDeviceIdToUuid,
  postLoginByPhoneCode,
} from '@whelk/services/src/user';
import { initAnonymousUser, login, setInitUserLoading } from '@whelk/store/slices/userSlice';
import {
  getUuid,
  setUuid,
  setOpenID,
  setUnionID,
  checkToken,
  setDeviceId,
  getStorageToken,
  setStorageToken,
  setAnonymousUser,
  removeStorageToken,
  removeStorageUnionID,
  checkDeviceId,
} from '@whelk/utils/src/checkToken';
import { isMiniProgram } from '@whelk/utils/src/checkUA';
import { useHash } from '@whelk/utils/src/hash';
import { initSentryUserParams } from '@whelk/utils/src/sentry';
import { trackSCGlobalProperty } from '@whelk/utils/src/track';
import useGoogleOneTap from './useGoogleOneTap';
import { loadHistoriesSilently } from './useHistoryList';

const isValidUUIDv4 = (uuid: string) => {
  const regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return regex.test(uuid);
};

// 首页判断 有的不需要跳转首页
export const judgeJumpHome = () => {
  if (checkIsNeedTokenPath()) {
    location.href = HOME_URL;
  }
};

const useInitLogin = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // Google One Tap
  useGoogleOneTap();
  // 注册 DeviceId
  const registerDeviceId = async () => {
    if (checkDeviceId()) return;
    const uuidFromGetUuid = getUuid();
    // 如果uuid没有或者不合法，重新生成uuid
    const uuid = uuidFromGetUuid && isValidUUIDv4(uuidFromGetUuid) ? uuidFromGetUuid : uuidv4();

    if (uuid) {
      setUuid(uuid);
      const { deviceIDStr } = await getDeviceIdToUuid(uuid);
      setDeviceId(deviceIDStr);
    }
  };

  // 获取用户信息
  const getUserInfo = async () => {
    const { userInfo } = await getCurrentUser();
    if (userInfo) {
      if (userInfo.phone || (isOverseas && userInfo)) {
        const newUser = {
          userID: userInfo?.userID,
          realUserID: userInfo?.realUserID,
          userName: userInfo?.name,
          webAvatar: userInfo?.avatarInfo,
        };
        dispatch(login(newUser));
      }
      const { userID, realUserID } = userInfo;
      dispatch(initAnonymousUser({ userID, realUserID }));
      setAnonymousUser(userID, realUserID);
      initSentryUserParams(userInfo);
      return true;
    }
  };

  const onGetCurrentUser = async () => {
    if (!checkToken()) {
      judgeJumpHome();
      return false;
    }

    try {
      // 续约token 有可能token过期
      // const { token } = await getNewToken();
      const [tokenResult] = await Promise.all([getNewToken(), getUserInfo()]);
      await loadHistoriesSilently(dispatch);
      const { token } = tokenResult;
      if (token) {
        setStorageToken(token);
      }
    } catch (err: any) {
      removeStorageToken();
      removeStorageUnionID();
      return false;
    }
  };

  const loginByAnonymous = async (trackParams?: Record<string, string>) => {
    try {
      const res = await postLoginByPhoneCode({
        loginType: LOGIN_TYPE.LOGINTYPEANONYMOUS,
        adInfo: trackParams,
      });

      if (!res) return;
      dispatch(login(res));
    } catch (error) {
      console.error('匿名登陆失败，', error);
    }
  };

  // 匿名登陆逻辑
  const anonymousLogin = async () => {
    try {
      // 推广活动字段，用拼接的后缀查询参数来判断当前用户来源，便于统计
      const search = new URLSearchParams(location.search);
      const trackParams: Record<string, string> = {};
      Array.from(search?.entries()).forEach(([key, val]) => {
        trackParams[key] = val;
      });
      loginByAnonymous(trackParams);
    } catch (error) {
      console.error('匿名登陆失败，', error);
    }
  };

  const initUserInfo = async () => {
    dispatch(setInitUserLoading(true));
    // 注册匿名用户设备id
    await registerDeviceId();
    // 应当检测token，只有没有token的情况下才会进行匿名登陆
    if (!checkToken() && !isOverseas) {
      // 获取到用户设备信息后进行匿名登陆
      await anonymousLogin();
    }
    // 获取已登录用户信息
    await onGetCurrentUser();
    dispatch(setInitUserLoading(false));
  };

  // ---------------------------- 小程序 token 注册逻辑 ----------------------------
  const { getHash, addEvent, onceEvent } = useHash();
  const oldHistoryLength = useRef(0);

  const initMiniProgramUser = () => {
    if (isMiniProgram) {
      const hash = getHash();
      // 存储小程序的uuid、openID、unionID
      hash?.uuid && setUuid(hash?.uuid);
      hash?.openID && setOpenID(hash?.openID);
      hash?.unionID && setUnionID(hash?.unionID);
      trackSCGlobalProperty({
        open_id: hash?.openID,
        union_id: hash?.unionID,
      });

      window.document.title = t('sidebar_hailuo_ai');
      // 如果是小程序 需要查看hash中是否有token
      if (hash.token) setStorageToken(hash.token);
      else removeStorageToken();
    }
  };

  // 搞不懂，此处需要@ 奕轩老师
  const onMiniProgramOnceEvent = () => {
    onceEvent((v) => {
      // 初始化token只需要执行一次
      if (isMiniProgram) {
        if (v.token) {
          setStorageToken(v.token);
          getUserInfo();
          addEvent((v) => {
            // 每次hash变化都会执行
            if (isMiniProgram) {
              if (v.token) return;
              // 浏览器前进时，没有token，删除之前缓存的token
              if (history.length > oldHistoryLength.current) {
                removeStorageToken();
              } else if (history.length < oldHistoryLength.current) {
                // 浏览器后退时，没有token，跳转到首页带有token的url
                const token = getStorageToken();
                history.replaceState({}, '', location.href.split('#')[0] + `#token=${token})}`);
                oldHistoryLength.current = history.length;
              }
            }
          });
        }
      }
    });
  };

  useEffect(() => {
    if (!isNative) {
      initUserInfo();
      initMiniProgramUser();
      onMiniProgramOnceEvent();
    }
  }, []);

  return {
    initUserInfo: initUserInfo,
    initMiniProgramUser: initMiniProgramUser,
    onMiniProgramOnceEvent: onMiniProgramOnceEvent,
  };
};

export default useInitLogin;
